import React, { Fragment, useRef, useState, useEffect } from 'react';
import { Transition } from '@headlessui/react';

interface FixedPopoverProps {
  trigger: React.ReactNode;
  children: React.ReactNode;
  position?: 'top' | 'bottom' | 'left' | 'right';
  offset?: number;
  className?: string;
  width?: string;
  onOpen?: () => void;
  onClose?: () => void;
  popoverClassName?: string;
  style?: React.CSSProperties;
}

export default function FixedPopover({
  trigger,
  children,
  position = 'top',
  offset = 40,
  className = '',
  width = 'w-64',
  onOpen,
  onClose,
  popoverClassName = '',
  style
}: FixedPopoverProps) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    const updatePosition = () => {
      if (triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();

        let top = 0;
        let left = 0;

        switch (position) {
          case 'top':
            top = rect.top - offset;
            left = rect.left + rect.width / 2;
            break;
          case 'bottom':
            top = rect.bottom + offset;
            left = rect.left + rect.width / 2;
            break;
          case 'left':
            top = rect.top + rect.height / 2 - 24;
            left = rect.left + offset;
            break;
          case 'right':
            top = rect.top + rect.height / 2 - 24;
            left = rect.right - offset;
            break;
        }

        setPopoverPosition({ top, left });
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('scroll', updatePosition);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition);
        window.removeEventListener('resize', updatePosition);
      };
    }
  }, [isOpen, position, offset]);

  const handleOpen = () => {
    setIsOpen(true);
    if (onOpen) {
      onOpen();
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) {
      onClose();
    }
  };

  return (
    <div className={`relative inline-block ${popoverClassName}`} style={style}>
      <div ref={triggerRef} onClick={handleOpen} className="flex h-full cursor-pointer items-center justify-center">
        {trigger}
      </div>

      <Transition
        show={isOpen}
        as={Fragment}
        enter="transition ease-out duration-200"
        enterFrom="opacity-0 translate-y-1"
        enterTo="opacity-100 translate-y-0"
        leave="transition ease-in duration-150"
        leaveFrom="opacity-100 translate-y-0"
        leaveTo="opacity-0 translate-y-1"
      >
        <div
          className="fixed z-[60]"
          style={{
            top: `${popoverPosition.top}px`,
            left: `${popoverPosition.left}px`,
            transform: `translate(${position === 'right' ? '0, -100%' : '-100%, -100%'})`
          }}
        >
          <div className={`rounded-md border bg-white shadow-lg ${width} ${className}`}>{children}</div>
        </div>
      </Transition>

      {isOpen && <div className="fixed inset-0 z-40" onClick={handleClose} />}
    </div>
  );
}
